import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import { buildMessageLink } from './messagesModel';
import './Marketplace.css';

const removeFavorite = (targetType, targetId) => api.delete(`/favorites/${targetType}/${targetId}`);

export default function ProductDetail() {
  const { language, translatePhrase } = useLanguage();
  const text = language === 'zh' ? {
    loading: '加载中...',
    sellerFallback: '卖家',
    saved: '已收藏商品',
    unsaved: '已取消收藏',
    negotiable: '支持议价',
    fixed: '价格固定',
    description: '商品描述',
    reminder: '交易提醒',
    reminderText: '建议当面验货、通过平台聊天沟通，不提前大额转账，保存沟通记录。',
    seller: '卖家',
    area: '交易地区',
    contact: '联系卖家',
    savedButton: '已收藏',
    saveButton: '收藏商品',
    report: '举报',
    reported: '举报已记录，平台会尽快处理',
    reportFailed: '举报提交失败，请稍后重试',
    reporting: '提交中...',
    favoriteFailed: '收藏操作失败，请稍后重试',
    safety: '二手商品与技能服务共用用户、聊天、举报和评价基础能力。',
  } : {
    loading: 'Loading...',
    sellerFallback: 'Seller',
    saved: 'Item saved',
    unsaved: 'Removed from saved items',
    negotiable: 'Negotiable',
    fixed: 'Fixed price',
    description: 'Item description',
    reminder: 'Trading reminder',
    reminderText: 'Inspect the item in person, communicate through platform chat, avoid large advance transfers, and keep chat records.',
    seller: 'Seller',
    area: 'Trading area',
    contact: 'Contact seller',
    savedButton: 'Saved',
    saveButton: 'Save item',
    report: 'Report',
    reported: 'Report recorded. The platform will review it soon.',
    reportFailed: 'Failed to submit report. Please try again later.',
    reporting: 'Submitting...',
    favoriteFailed: 'Failed to update saved item. Please try again later.',
    safety: 'Secondhand items share the same user, chat, report, and review foundation as skill services.',
  };
  const { id } = useParams();
  const [liveItem, setLiveItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [notice, setNotice] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [favoriteSubmitting, setFavoriteSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLiveItem(null);
    if (!id.startsWith('listing-')) {
      setMissing(true);
      setLoading(false);
      return;
    }
    const liveId = id.replace('listing-', '');
    setLoading(true);
    setMissing(false);
    api.get(`/marketplace/products/${liveId}`)
      .then(res => setLiveItem(res.data))
      .catch(() => setMissing(true))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id.startsWith('listing-')) {
      setSaved(false);
      return;
    }
    const targetId = Number(id.replace('listing-', ''));
    api.get('/favorites', { params: { targetType: 'product' } })
      .then(res => {
        const favorites = res.data.favorites || [];
        setSaved(favorites.some(item => Number(item.target_id) === targetId));
      })
      .catch(() => setSaved(false));
  }, [id]);

  if (loading) return <div className="loading">{text.loading}</div>;
  if (missing || !liveItem) return <Navigate to="/secondhand" replace />;
  const item = liveItem;
  const messageLink = buildMessageLink({
    peerId: liveItem?.sellerId,
    peerName: item.seller || text.sellerFallback,
    peerSeed: item.seller || item.id,
  });
  const showNotice = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2600);
  };

  const submitReport = async () => {
    setReportSubmitting(true);
    try {
      const res = await api.post('/reports', {
        targetType: 'product',
        targetId: liveItem.id,
        reason: language === 'zh' ? '商品内容疑似违规' : 'Item content may violate platform rules',
      });
      showNotice(res.data.message || text.reported);
    } catch (err) {
      showNotice(err.response?.data?.error || text.reportFailed);
    } finally {
      setReportSubmitting(false);
    }
  };

  const toggleSaved = async () => {
    const targetType = 'product';
    const targetId = liveItem.id;
    setFavoriteSubmitting(true);
    try {
      if (saved) {
        await removeFavorite(targetType, targetId);
        setSaved(false);
        showNotice(text.unsaved);
      } else {
        const res = await api.post('/favorites', { targetType, targetId });
        setSaved(true);
        showNotice(res.data.message || text.saved);
      }
    } catch (err) {
      showNotice(err.response?.data?.error || text.favoriteFailed);
    } finally {
      setFavoriteSubmitting(false);
    }
  };

  return (
    <div className="market-page">
      <div className="container detail-layout">
        <main className="detail-card">
          <div className="card-meta">
            <span className="pill">{translatePhrase(item.category)}</span>
            <span className="pill">{translatePhrase(item.condition)}</span>
            <span className="pill">{translatePhrase(item.area)} · {item.distance}</span>
          </div>
          <h1 className="detail-title">{translatePhrase(item.title)}</h1>
          <p className="card-muted detail-lead-summary">{translatePhrase(item.summary)}</p>
          <div className="price-line">
            <span className="price">{item.price}</span>
            <span className="card-muted">{item.negotiable ? text.negotiable : text.fixed}</span>
          </div>
          <section className="detail-section">
            <h2>{text.description}</h2>
            <p className="card-muted" style={{ marginTop: 10 }}>{translatePhrase(item.details)}</p>
          </section>
          <section className="detail-section">
            <h2>{text.reminder}</h2>
            <p className="card-muted">{text.reminderText}</p>
          </section>
        </main>
        <aside className="detail-card">
          <h2>{text.seller} {translatePhrase(item.seller)}</h2>
          <p className="card-muted" style={{ margin: '8px 0 16px' }}>{text.area}: {translatePhrase(item.area)}</p>
          {notice && <div className="detail-notice">{notice}</div>}
          <div className="side-actions">
            <Link className="btn btn-primary" to={messageLink}>{text.contact}</Link>
            <button className="btn btn-ghost" type="button" onClick={toggleSaved} disabled={favoriteSubmitting}>{saved ? text.savedButton : text.saveButton}</button>
            <button className="btn btn-outline" type="button" onClick={submitReport} disabled={reportSubmitting}>
              {reportSubmitting ? text.reporting : text.report}
            </button>
          </div>
          <div className="safety-box" style={{ marginTop: 18 }}>{text.safety}</div>
        </aside>
      </div>
    </div>
  );
}
